import { NextRequest, NextResponse } from "next/server"

import { normalizePathname } from "../utils/localePath"
import { resolveDefaultLocaleRedirectPathname, type RedirectDefaultLocalePrefixOptions } from "./localeRedirectPath"

export type { RedirectDefaultLocalePrefixOptions }
export { resolveDefaultLocaleRedirectPathname }

/** 默认语言去前缀重定向：永久跳转，利于 SEO 归并 canonical URL */
const PERMANENT_REDIRECT_STATUS = 308

const WWW_HOST_REGEXP = /^(test)?www\.metal\.com(?::\d+)?$/i

function resolveAbsoluteRedirectUrl(request: NextRequest, location: string): URL | null {
  try {
    if (location.startsWith("/")) {
      // 相对路径：基于反代后的真实 Host/协议解析，避免内部上游 host 校验失败，同时保留 query/hash
      const base = new URL(request.nextUrl.href)
      applyRequestOrigin(base, request)
      return new URL(location, base)
    }
    return new URL(location)
  } catch {
    return null
  }
}

function buildAbsoluteRedirectUrl(request: NextRequest, targetPathname: string): URL | null {
  try {
    const redirectUrl = new URL(request.nextUrl.href)
    applyRequestOrigin(redirectUrl, request)
    redirectUrl.pathname = targetPathname
    return redirectUrl
  } catch {
    return null
  }
}

function getRequestHost(request: NextRequest): string {
  const forwardedHost = request.headers.get("x-forwarded-host")?.split(",")[0]?.trim()
  const host = request.headers.get("host")?.split(",")[0]?.trim()
  return forwardedHost || host || request.nextUrl.host
}

function hasExplicitPort(host: string): boolean {
  return host.startsWith("[") ? host.includes("]:") : host.includes(":")
}

function applyRequestOrigin(url: URL, request: NextRequest): void {
  const host = getRequestHost(request)
  url.host = host
  if (!hasExplicitPort(host)) {
    url.port = ""
  }
  url.protocol = resolveRedirectProtocol(request, url.protocol)
}

/** 仅信任 http/https 的转发协议，避免 ftp 等非法值拼进 Location */
function resolveRedirectProtocol(request: NextRequest, fallbackProtocol: string): string {
  const forwarded = request.headers.get("x-forwarded-proto")?.split(",")[0]?.trim().toLowerCase()
  if (forwarded === "http" || forwarded === "https") {
    return `${forwarded}:`
  }
  return fallbackProtocol
}

export function isWwwMetalHost(request: NextRequest): boolean {
  const host = getRequestHost(request)
  return Boolean(host && WWW_HOST_REGEXP.test(host))
}

/**
 * 将含默认语言前缀（如 /en）的 URL 308 重定向到无前缀的规范英文 URL。
 *
 * - www 主站：/en/contact-us -> /contact-us，/en/industry/foo -> /industry/foo
 * - www 子站（反向代理）：/en/foo -> /{subproject}/foo
 * - 独立域名：/en/foo -> /foo
 */
export function redirectDefaultLocalePrefix(
  request: NextRequest,
  options: RedirectDefaultLocalePrefixOptions = {}
): NextResponse | null {
  const defaultLocale = options.defaultLocale ?? "en"
  const localeCookieName = options.localeCookieName ?? "locale"
  const pathname = normalizePathname(request.nextUrl.pathname)
  const targetPathname = resolveDefaultLocaleRedirectPathname(pathname, {
    ...options,
    isWww: isWwwMetalHost(request),
  })

  if (!targetPathname || targetPathname === pathname) {
    return null
  }

  // middleware 要求绝对 URL；基于真实请求 Host 构造目标并保留 query
  const redirectUrl = buildAbsoluteRedirectUrl(request, targetPathname)
  if (!redirectUrl) {
    return null
  }

  const response = NextResponse.redirect(redirectUrl, PERMANENT_REDIRECT_STATUS)
  response.cookies.set(localeCookieName, defaultLocale, { path: "/", sameSite: "lax" })

  return response
}

/**
 * 将 next-intl 对默认语言前缀（/en）产生的 307/308 重定向统一为 308。
 * 仅处理原始请求路径含 /en 的场景，避免影响其他语言的 locale 检测跳转。
 */
export function promoteTemporaryRedirectToPermanent(
  request: NextRequest,
  response: NextResponse,
  defaultLocale = "en"
): NextResponse {
  if (response.status !== 307 && response.status !== 308) {
    return response
  }

  const requestSegments = normalizePathname(request.nextUrl.pathname).split("/").filter(Boolean)
  if (requestSegments[0] !== defaultLocale) {
    return response
  }

  const location = response.headers.get("location")
  if (!location) {
    return response
  }

  const redirectUrl = resolveAbsoluteRedirectUrl(request, location)
  if (!redirectUrl) {
    return response
  }

  const permanent = NextResponse.redirect(redirectUrl, PERMANENT_REDIRECT_STATUS)

  response.headers.forEach((value, key) => {
    if (key.toLowerCase() === "location") {
      return
    }
    permanent.headers.set(key, value)
  })

  response.cookies.getAll().forEach((cookie) => {
    permanent.cookies.set(cookie)
  })

  return permanent
}
